import { useMemo, useState } from 'react';
import {
  LEVEL_LABELS,
  LEVEL_BADGE_VARIANT,
  filterTree,
  scopeCheckState,
} from '../lib/taxonomy';
import Badge from './Badge';

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TreeRow({
  node,
  depth,
  selectedId,
  onSelect,
  expanded,
  onToggleExpand,
  mode,
  inScopeSet,
  onToggleScope,
  readOnly,
}) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node.id);
  const checkState = mode === 'scope' ? scopeCheckState(node, inScopeSet) : null;

  return (
    <>
      <div
        className={`tax-row${selectedId === node.id ? ' selected' : ''}`}
        style={{ paddingLeft: 10 + depth * 14 }}
        onClick={() => onSelect?.(node)}
      >
        <button
          type="button"
          className={`tax-toggle${hasChildren ? '' : ' leaf'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggleExpand(node.id);
          }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? (isOpen ? '▾' : '▸') : ''}
        </button>
        {mode === 'scope' && (
          <button
            type="button"
            className={`tax-check${checkState === 'checked' ? ' checked' : ''}${checkState === 'partial' ? ' partial' : ''}`}
            disabled={readOnly}
            onClick={(e) => {
              e.stopPropagation();
              onToggleScope?.(node, checkState !== 'checked');
            }}
            aria-label="Toggle scope"
          >
            <CheckIcon />
          </button>
        )}
        <Badge variant={LEVEL_BADGE_VARIANT[node.level] || ''} style={{ fontSize: 9, padding: '1px 5px' }}>
          {LEVEL_LABELS[node.level]}
        </Badge>
        <span className="tax-label">{node.name}</span>
        <span className="tax-code">{node.code}</span>
      </div>
      {hasChildren && isOpen &&
        node.children.map((child) => (
          <TreeBranch
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
            mode={mode}
            inScopeSet={inScopeSet}
            onToggleScope={onToggleScope}
            readOnly={readOnly}
          />
        ))}
    </>
  );
}

function TreeBranch(props) {
  return <TreeRow {...props} />;
}

/**
 * Hierarchical process taxonomy tree — browse (admin) or scope-select (IN-00).
 */
export default function TaxonomyTree({
  tree,
  selectedId,
  onSelect,
  search = '',
  mode = 'browse',
  inScopeSet = new Set(),
  onToggleScope,
  readOnly = false,
  defaultExpandedDepth = 2,
}) {
  const filtered = useMemo(() => filterTree(tree, search), [tree, search]);
  const [expanded, setExpanded] = useState(() => new Set());

  const ensureDefaultExpanded = useMemo(() => {
    const ids = new Set();
    const walk = (nodes, depth) => {
      for (const n of nodes || []) {
        if (depth < defaultExpandedDepth) ids.add(n.id);
        if (n.children?.length) walk(n.children, depth + 1);
      }
    };
    walk(tree, 0);
    return ids;
  }, [tree, defaultExpandedDepth]);

  const effectiveExpanded = expanded.size ? expanded : ensureDefaultExpanded;

  function toggleExpand(id) {
    setExpanded((prev) => {
      const base = prev.size ? prev : new Set(ensureDefaultExpanded);
      const next = new Set(base);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!filtered.length) {
    return <div className="empty" style={{ padding: 20 }}>No nodes match your search.</div>;
  }

  return (
    <div className="tax-body">
      {filtered.map((node) => (
        <TreeBranch
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          expanded={effectiveExpanded}
          onToggleExpand={toggleExpand}
          mode={mode}
          inScopeSet={inScopeSet}
          onToggleScope={onToggleScope}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}
